import React from "react";
import useMediaQuery, { MOBILE_QUERY } from "../hooks/useMediaQuery.js";
import {
  ChevronDown,
  ChevronUp,
  Pin,
} from "lucide-react";
import {
  ORDER_ACTIONS_COLUMN,
  ORDER_ADDRESS_FIELDS,
  ORDER_BILLING_APPROVAL_COLUMN,
  ORDER_DISPATCH_COLUMN,
  buildOrderGridColumnStyle,
  ORDER_GRID_COLUMNS,
  ORDER_GRID_SELECTION_COLUMN_WIDTH,
  canDeleteOrder,
  createEditableOrderDraft,
  getOrderAddressValidationKey,
  getOrderDraftErrors,
  getOrderGridTrailingColumns,
  isOrderExecutionRelevant,
} from "../hooks/useDataManagerOrdersUtils.js";
import useSortableColumnHeaderDrag from "../hooks/useSortableColumnHeaderDrag.js";
import {
  DATA_MANAGER_ACTIVE_SORT_HEADER_CLASS_NAME,
  DATA_MANAGER_ACTIVE_SORT_ICON_WRAP_CLASS_NAME,
  DATA_MANAGER_ACTIVE_SORT_LABEL_CLASS_NAME,
  resolveDataManagerActiveSortState,
  resolveDataManagerHeaderAriaSort,
} from "../utils/dataManagerTableSort.js";
import useDataManagerTableScrollRestore from "../hooks/useDataManagerTableScrollRestore.js";
import {
  buildFrozenColumnCellStyle,
  buildFrozenColumnLayout,
  buildPinnedSectionDescriptors,
  DATA_MANAGER_FROZEN_HEADER_CELL_CLASS_NAME,
  DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME,
  DATA_MANAGER_FROZEN_PIN_ICON_CLASS_NAME,
  DATA_MANAGER_FROZEN_PIN_ICON_STROKE_WIDTH,
  dedupeOrderGridColumnsByKey,
  ORDER_GRID_SELECTION_COLUMN_KEY,
  splitColumnsByFrozenState,
} from "../utils/dataManagerFrozenPanes.js";
import ColumnHeaderDragOverlay from "./ColumnHeaderDragOverlay.jsx";
import DataManagerOrdersColumnContextMenu from "./DataManagerOrdersColumnContextMenu.jsx";
import DataManagerOrdersGridHeader from "./DataManagerOrdersGridHeader.jsx";
import OrderGridRow from "./DataManagerOrdersGridRow.jsx";
const GRID_HEADER_CELL_CLASS_NAME = "border-b border-slate-200 bg-slate-50/95 px-2 py-1.5 text-left text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-800";
const SORTABLE_HEADER_BUTTON_CLASS_NAME = "inline-flex w-full items-center gap-1 text-left transition-colors duration-150";
function OrderColumnResizeHandle({ columnKey = "", label = "", onPointerDown = null }) {
  if (!columnKey) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={`Redimensionner la colonne ${label}`}
      className="absolute inset-y-0 right-0 z-10 w-3 translate-x-1/2 cursor-col-resize touch-none select-none bg-transparent p-0 after:absolute after:bottom-1 after:left-1/2 after:top-1 after:w-px after:-translate-x-1/2 after:rounded-full after:bg-[rgba(148,163,184,0.38)] hover:after:bg-[rgba(15,118,110,0.72)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/25"
      onClick={(event) => {
        event.preventDefault();
      }}
      onPointerDown={(event) => {
        onPointerDown?.(event, columnKey);
      }}
      title={`Redimensionner la colonne ${label}`}
    />
  );
}

function OrderResizableHeaderContent({ children, columnKey = "", label = "", onResizePointerDown = null }) {
  return (
    <div className="relative min-w-0 pr-2.5">
      {children}
      <OrderColumnResizeHandle columnKey={columnKey} label={label} onPointerDown={onResizePointerDown} />
    </div>
  );
}

function OrderSortableHeaderCell({
  canFreezePanes = false,
  className = "",
  columnKey,
  draggable = false,
  frozenLayoutEntry = null,
  isFrozenColumn = false,
  label,
  help,
  onColumnContextMenu = null,
  onColumnMove = null,
  onResizePointerDown = null,
  onSort,
  sortState,
  style = null,
}) {
  const { direction: activeSortDirection, isActiveSort } = resolveDataManagerActiveSortState(sortState, columnKey);
  const SortIndicatorIcon = activeSortDirection === "desc" ? ChevronDown : ChevronUp;
  const thRef = React.useRef(null);
  const { dragOverlay, isDragging } = useSortableColumnHeaderDrag({
    columnKey,
    columnKeyAttribute: "data-order-column-key",
    draggable,
    label,
    onMove: onColumnMove,
    thRef,
  });

  return (
    <th
      ref={thRef}
      title={help}
      data-order-column-key={draggable ? columnKey : undefined}
      aria-sort={resolveDataManagerHeaderAriaSort(sortState, columnKey)}
      className={[
        GRID_HEADER_CELL_CLASS_NAME,
        className,
        isFrozenColumn ? DATA_MANAGER_FROZEN_HEADER_CELL_CLASS_NAME : "",
        isFrozenColumn && frozenLayoutEntry?.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : "",
        isActiveSort ? DATA_MANAGER_ACTIVE_SORT_HEADER_CLASS_NAME : "",
        draggable ? "cursor-grab select-none" : "",
        isDragging ? "border border-dashed border-accent/40 bg-slate-50/30 opacity-30" : "",
      ].filter(Boolean).join(" ")}
      style={buildFrozenColumnCellStyle(frozenLayoutEntry, style, { isHeader: true })}
      onClick={() => onSort?.(columnKey)}
      onContextMenu={canFreezePanes ? (event) => {
        event.preventDefault();
        event.stopPropagation();
        onColumnContextMenu?.({
          canFreezePanes: true,
          columnKey,
          columnLabel: label,
          isColumnFrozen: isFrozenColumn,
          x: event.clientX,
          y: event.clientY,
        });
      } : undefined}
    >
      <OrderResizableHeaderContent columnKey={columnKey} label={label} onResizePointerDown={onResizePointerDown}>
        <span className={[
          SORTABLE_HEADER_BUTTON_CLASS_NAME,
          "min-w-0 justify-start pr-1",
          isFrozenColumn ? "font-semibold text-accent-strong" : "",
          isActiveSort ? DATA_MANAGER_ACTIVE_SORT_LABEL_CLASS_NAME : "text-slate-800",
        ].join(" ")}>
          {isFrozenColumn ? (
            <Pin
              aria-hidden="true"
              className={DATA_MANAGER_FROZEN_PIN_ICON_CLASS_NAME}
              size={12}
              strokeWidth={DATA_MANAGER_FROZEN_PIN_ICON_STROKE_WIDTH}
            />
          ) : null}
          <span className="truncate">{label}</span>
          {isActiveSort ? (
            <span className={DATA_MANAGER_ACTIVE_SORT_ICON_WRAP_CLASS_NAME}>
              <SortIndicatorIcon
                aria-hidden="true"
                size={14}
                strokeWidth={2.5}
              />
            </span>
          ) : null}
        </span>
      </OrderResizableHeaderContent>
      <ColumnHeaderDragOverlay dragOverlay={dragOverlay} />
    </th>
  );
}

export default function DataManagerOrdersGrid({
  productCatalog = [],
  activeFilterChips = [],
  advancedFilters = {},
  allVisibleDeletableOrdersSelected = false,
  allVisibleSelectableOrdersSelected = false,
  addressValidationErrors = {},
  addressValidationPendingKeys = {},
  canApplyEndOfDayLoadGroup = false,
  canOpenConsolidation = false,
  canAssignEndOfDayLoad = false,
  canClearDeliveredOrders = false,
  canDeleteDeliveredOrders = false,
  canEditOrders = false,
  canEditOrderExecution = false,
  deletableVisibleOrders = [],
  draftVisibleFilterKeys = [],
  draftAdvancedFilters = {},
  endOfDayLoadGroupReason = "",
  hasAdvancedFiltersDraft = false,
  isAdvancedFiltersOpen = false,
  isCustomizingFilters = false,
  isPendingBillingApprovalFilter = false,
  editingOrderFocusKey = "client_code",
  editingOrderId = null,
  freezableOrderColumnDescriptors = [],
  frozenColumnKeys = [],
  frozenColumnLabels = [],
  hasFrozenPanes = false,
  isFrozenColumn = () => false,
  onToggleColumnFreeze = null,
  onUnfreezePanes = null,
  focusedOrderId = null,
  onFocusOrderRow = null,
  emptyStateMessage = "",
  isBrowseMode = false,
  isCreatingOrder = false,
  isEndOfDayLoadPending = false,
  isDeletingSelection = false,
  isBulkBillingApprovalPending = false,
  bulkBillingApprovalSelectionMeta = null,
  isGlobalSearchMode = false,
  isPending,
  isRecentBrowseLimited = false,
  loadingClientOptions = [],
  loadingClientOptionsByLookupKey = {},
  onApplyAdvancedFilters = null,
  onApproveForBilling = null,
  onClearAllFilters = null,
  onClearSearchQuery = null,
  onColumnMove = null,
  onColumnResizeEnd = null,
  onDraftAdvancedFilterChange = null,
  onExportPdf = null,
  deleteSelectionBlockedReason = "",
  onResetFilterSettings = null,
  onOpenEndOfDayLoadGroupDialog = null,
  onOpenBulkBillingApproval = null,
  onOpenOrderBrowser = null,
  onQuickSelectOrderBrowseDate = null,
  onRemoveActiveFilter = null,
  onResetAdvancedFilters = null,
  onResetOrderScope = null,
  onSaveFilterSettings = null,
  onSearchQueryChange = null,
  onToggleAdvancedFilters = null,
  onToggleFilterSettings = null,
  onTogglePendingBillingApprovalFilter = null,
  onToggleVisibleFilter = null,
  onSort = null,
  onToggleAllVisibleOrderSelections = null,
  onAssignEndOfDayLoad = null,
  onCancelEditingOrder = null,
  onClearDeliveredOrder = null,
  onSaveLoadingReference = null,
  onEditOrderExecution = null,
  onOrderAddressEnterValidation = null,
  onOrderSelectionChange = null,
  onSaveOrder = null,
  onStartEditingOrder = null,
  onUpdateOrderDraftValue = null,
  deliveryDatePickerValue = "",
  orderDefaultFocusKey = "client_code",
  orderDrafts = {},
  orderTableColumnStyles = {},
  orderTableStyle = null,
  registerAddressValidationFieldRef = null,
  resolvedOrderColumns = ORDER_GRID_COLUMNS,
  resultSummaryLabel = "",
  searchInputRef = null,
  searchQuery = "",
  selectedDeleteCount = 0,
  selectedOrderCount = 0,
  selectedOrderIds = {},
  pendingBillingApprovalCount = 0,
  selectableVisibleOrders = [],
  showBulkDeleteControls = false,
  showBulkBillingApprovalControls = false,
  showEndOfDayLoadGroupControls = false,
  showSelectionControls = false,
  sortedOrders = [],
  sortState = { direction: "asc", key: null },
  totalOrderCount = 0,
  activeSearchQuery = "",
  trimmedSearchQuery = "",
  visibleFilterKeys = [],
  visibleOrderCount = 0,
}) {
  const ordersScrollContainerRef = useDataManagerTableScrollRestore("orders", visibleOrderCount);
  const mobile = useMediaQuery(MOBILE_QUERY);
  const GridTable = mobile ? "div" : "table";
  const GridBody = mobile ? "div" : "tbody";
  const [columnContextMenuState, setColumnContextMenuState] = React.useState(null);
  const visibleDataColumns = resolvedOrderColumns;
  const trailingGridColumns = React.useMemo(() => getOrderGridTrailingColumns(), []);
  const trailingColumnKeySet = React.useMemo(
    () => new Set(trailingGridColumns.map((column) => column.key)),
    [trailingGridColumns],
  );
  const allFreezableGridColumns = React.useMemo(
    () => dedupeOrderGridColumnsByKey([...visibleDataColumns, ...trailingGridColumns]),
    [trailingGridColumns, visibleDataColumns],
  );
  const { pinnedColumns, scrollableColumns } = React.useMemo(
    () => splitColumnsByFrozenState(allFreezableGridColumns, frozenColumnKeys),
    [allFreezableGridColumns, frozenColumnKeys],
  );
  const pinSelectionColumn = showSelectionControls && pinnedColumns.length > 0;
  const pinnedSectionDescriptors = React.useMemo(
    () => buildPinnedSectionDescriptors({
      freezableColumnDescriptors: freezableOrderColumnDescriptors,
      frozenColumnKeys,
      pinSelectionColumn,
    }),
    [freezableOrderColumnDescriptors, frozenColumnKeys, pinSelectionColumn],
  );
  const frozenColumnLayout = React.useMemo(
    () => buildFrozenColumnLayout(pinnedSectionDescriptors),
    [pinnedSectionDescriptors],
  );
  const renderOrderHeaderCell = (column) => (
    <OrderSortableHeaderCell
      key={column.key}
      canFreezePanes
      className={column.widthClassName || ""}
      columnKey={column.key}
      draggable={!!onColumnMove && !trailingColumnKeySet.has(column.key)}
      frozenLayoutEntry={frozenColumnLayout[column.key]}
      isFrozenColumn={isFrozenColumn(column.key)}
      label={column.label}
      help={column.help}
      onColumnContextMenu={setColumnContextMenuState}
      onColumnMove={onColumnMove}
      onResizePointerDown={onSort?.resizePointerDown}
      onSort={onSort?.sort}
      sortState={sortState}
      style={orderTableColumnStyles[column.key]}
    />
  );
  const selectableVisibleOrderIdSet = React.useMemo(
    () => new Set((Array.isArray(selectableVisibleOrders) ? selectableVisibleOrders : []).map((order) => order?.order_id).filter(Boolean)),
    [selectableVisibleOrders],
  );

  return (
    <section className="data-manager-viewport-panel min-w-0 rounded-lg border border-slate-200/90 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.025)]">
      <DataManagerOrdersGridHeader
        activeFilterChips={activeFilterChips}
        advancedFilters={advancedFilters}
        canApplyEndOfDayLoadGroup={canApplyEndOfDayLoadGroup}
        bulkBillingApprovalSelectionMeta={bulkBillingApprovalSelectionMeta}
        draftVisibleFilterKeys={draftVisibleFilterKeys}
        draftAdvancedFilters={draftAdvancedFilters}
        endOfDayLoadGroupReason={endOfDayLoadGroupReason}
        hasAdvancedFiltersDraft={hasAdvancedFiltersDraft}
        isAdvancedFiltersOpen={isAdvancedFiltersOpen}
        isCustomizingFilters={isCustomizingFilters}
        isBrowseMode={isBrowseMode}
        isPendingBillingApprovalFilter={isPendingBillingApprovalFilter}
        isEndOfDayLoadPending={isEndOfDayLoadPending}
        isCreatingOrder={isCreatingOrder}
        isDeletingSelection={isDeletingSelection}
        isBulkBillingApprovalPending={isBulkBillingApprovalPending}
        isRecentBrowseLimited={isRecentBrowseLimited}
        loadingClientOptions={loadingClientOptions}
        onApplyAdvancedFilters={onApplyAdvancedFilters}
        onClearAllFilters={onClearAllFilters}
        onClearSearchQuery={onClearSearchQuery}
        onDraftAdvancedFilterChange={onDraftAdvancedFilterChange}
        onExportPdf={onExportPdf}
        deleteSelectionBlockedReason={deleteSelectionBlockedReason}
        onResetFilterSettings={onResetFilterSettings}
        onOpenEndOfDayLoadGroupDialog={onOpenEndOfDayLoadGroupDialog}
        onOpenBulkBillingApproval={onOpenBulkBillingApproval}
        onOpenOrderBrowser={onOpenOrderBrowser}
        onQuickSelectOrderBrowseDate={onQuickSelectOrderBrowseDate}
        onRemoveActiveFilter={onRemoveActiveFilter}
        onResetAdvancedFilters={onResetAdvancedFilters}
        onResetOrderScope={onResetOrderScope}
        onSaveFilterSettings={onSaveFilterSettings}
        onSearchQueryChange={onSearchQueryChange}
        onToggleAdvancedFilters={onToggleAdvancedFilters}
        onToggleAllVisibleOrderSelections={onToggleAllVisibleOrderSelections}
        onToggleFilterSettings={onToggleFilterSettings}
        onTogglePendingBillingApprovalFilter={onTogglePendingBillingApprovalFilter}
        onToggleVisibleFilter={onToggleVisibleFilter}
        deliveryDatePickerValue={deliveryDatePickerValue}
        resultSummaryLabel={resultSummaryLabel}
        searchInputRef={searchInputRef}
        searchQuery={searchQuery}
        selectedDeleteCount={selectedDeleteCount}
        selectedOrderCount={selectedOrderCount}
        pendingBillingApprovalCount={pendingBillingApprovalCount}
        showBulkDeleteControls={showBulkDeleteControls}
        showBulkBillingApprovalControls={showBulkBillingApprovalControls}
        showEndOfDayLoadGroupControls={showEndOfDayLoadGroupControls}
        visibleFilterKeys={visibleFilterKeys}
        visibleOrderCount={visibleOrderCount}
      />

      {visibleOrderCount === 0 ? (
        <div className="px-2.5 py-3">
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              {totalOrderCount > 0 ? "Aucun résultat" : "Aucune commande"}
            </p>
            <p className="mt-1.5 text-[11.5px] leading-5 text-slate-600">{emptyStateMessage}</p>
          </div>
        </div>
      ) : (
        <>
          {hasFrozenPanes ? (
            <div className="data-manager-frozen-toolbar flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 px-2.5 py-2">
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
                  Volets figés
                </span>
                {frozenColumnLabels.map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 rounded-[4px] border border-accent/25 bg-white px-2 py-0.5 text-[10.5px] font-semibold text-accent-strong"
                  >
                    <Pin
                      aria-hidden="true"
                      className={DATA_MANAGER_FROZEN_PIN_ICON_CLASS_NAME}
                      size={11}
                      strokeWidth={DATA_MANAGER_FROZEN_PIN_ICON_STROKE_WIDTH}
                    />
                    {label}
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="shrink-0 rounded-[4px] border border-slate-300 bg-white px-3 py-1 text-[10.5px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
                onClick={() => onUnfreezePanes?.()}
              >
                Dégeler les volets
              </button>
            </div>
          ) : null}
        <div
          ref={ordersScrollContainerRef}
          className={`data-manager-viewport-grid ${mobile ? "mobile-record-scroll" : ""} max-h-[min(52rem,calc(100dvh-19rem))] min-w-0 overflow-auto overscroll-contain`}
        >
          <GridTable className={mobile ? "mobile-record-grid" : "data-manager-frozen-grid min-w-max table-fixed border-collapse text-[10.5px] text-slate-700 tabular-nums"} style={mobile ? undefined : orderTableStyle}>
            {!mobile ? <thead className="sticky top-0 z-10 bg-slate-50/95 shadow-[0_1px_0_rgba(226,232,240,0.95)]">
              <tr>
                {showSelectionControls && pinSelectionColumn ? (
                  <th
                    className={[
                      `${GRID_HEADER_CELL_CLASS_NAME} w-11 text-center`,
                      DATA_MANAGER_FROZEN_HEADER_CELL_CLASS_NAME,
                      frozenColumnLayout[ORDER_GRID_SELECTION_COLUMN_KEY]?.isLastFrozen
                        ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME
                        : "",
                    ].filter(Boolean).join(" ")}
                    style={buildFrozenColumnCellStyle(
                      frozenColumnLayout[ORDER_GRID_SELECTION_COLUMN_KEY],
                      buildOrderGridColumnStyle(ORDER_GRID_SELECTION_COLUMN_WIDTH),
                      { isHeader: true },
                    )}
                  >
                    <input
                      type="checkbox"
                      aria-label="Sélectionner toutes les commandes visibles disponibles"
                      checked={allVisibleSelectableOrdersSelected}
                      disabled={selectableVisibleOrders.length === 0 || isDeletingSelection}
                      onChange={(event) => onToggleAllVisibleOrderSelections?.(event.target.checked)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-accent focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </th>
                ) : null}
                {pinnedColumns.map(renderOrderHeaderCell)}
                {showSelectionControls && !pinSelectionColumn ? (
                  <th className={`${GRID_HEADER_CELL_CLASS_NAME} w-11 text-center`}>
                    <input
                      type="checkbox"
                      aria-label="Sélectionner toutes les commandes visibles disponibles"
                      checked={allVisibleSelectableOrdersSelected}
                      disabled={selectableVisibleOrders.length === 0 || isDeletingSelection}
                      onChange={(event) => onToggleAllVisibleOrderSelections?.(event.target.checked)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-accent focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </th>
                ) : null}
                {scrollableColumns.map(renderOrderHeaderCell)}
                <th className={GRID_HEADER_CELL_CLASS_NAME} style={orderTableColumnStyles[ORDER_ACTIONS_COLUMN.key]}>
                  <OrderResizableHeaderContent
                    columnKey={ORDER_ACTIONS_COLUMN.key}
                    label={ORDER_ACTIONS_COLUMN.label}
                    onResizePointerDown={onSort?.resizePointerDown}
                  >
                    <span className="block truncate">Actions</span>
                  </OrderResizableHeaderContent>
                </th>
              </tr>
            </thead> : null}
            <GridBody className={mobile ? "mobile-record-grid" : "bg-white"}>
              {sortedOrders.map((order, rowIndex) => {
                const rowAddressValidationPending = ORDER_ADDRESS_FIELDS.some(
                  (fieldKey) => Boolean(addressValidationPendingKeys[getOrderAddressValidationKey(order.order_id, fieldKey)])
                );
                const rowPending = Boolean(
                  rowAddressValidationPending
                  || isDeletingSelection
                  || isPending(`order:delete:${order.order_id}`)
                  || isPending(`order:update:${order.order_id}`)
                  || isPending(`order:cancel:${order.order_id}`)
                  || isPending(`order:clear-delivered:${order.order_id}`)
                  || isPending(`order:execution:${order.order_id}`)
                  || isPending(`order:reopen:${order.order_id}`)
                  || isEndOfDayLoadPending
                );
                const draft = orderDrafts[order.order_id] || createEditableOrderDraft(order);
                const rowValidationErrors = {
                  ...(addressValidationErrors[getOrderAddressValidationKey(order.order_id, "address")]
                    ? { address: addressValidationErrors[getOrderAddressValidationKey(order.order_id, "address")] }
                    : {}),
                };
                const rowErrors = {
                  ...getOrderDraftErrors(draft, { clientCorrespondenceOptionsByLookupKey: loadingClientOptionsByLookupKey }),
                  ...rowValidationErrors,
                };
                const rowValidationError = rowValidationErrors.address || "";

                return (
                  <OrderGridRow
                    mobile={mobile}
                    productCatalog={productCatalog}
                    canOpenConsolidation={canOpenConsolidation}
                    canSelect={showSelectionControls && selectableVisibleOrderIdSet.has(order.order_id)}
                    clientCorrespondenceOptions={loadingClientOptions}
                    clientCorrespondenceOptionsByLookupKey={loadingClientOptionsByLookupKey}
                    columnStyles={orderTableColumnStyles}
                    frozenColumnLayout={frozenColumnLayout}
                    key={order.order_id}
                    rowIndex={rowIndex}
                    draft={draft}
                    editingFocusKey={editingOrderFocusKey}
                    errors={rowErrors}
                    getValidationKeyForField={(fieldKey) => getOrderAddressValidationKey(order.order_id, fieldKey)}
                    hasDraft={Boolean(orderDrafts[order.order_id])}
                    canAssignEndOfDayLoad={canAssignEndOfDayLoad}
                    canClearDeliveredOrder={canClearDeliveredOrders && order?.can_clear_delivered}
                    canEditOrders={canEditOrders}
                    canOpenExecution={isOrderExecutionRelevant(order, {
                      canWriteExecution: canEditOrderExecution && !order?.is_cancelled,
                    })}
                    canWriteExecution={canEditOrderExecution && !order?.is_cancelled}
                    isEditing={editingOrderId === order.order_id}
                    isFocused={focusedOrderId === order.order_id}
                    isPending={rowPending}
                    isSelected={Boolean(selectedOrderIds[order.order_id])}
                    onFocusRow={() => onFocusOrderRow?.(order.order_id)}
                    isValidatingAddress={rowAddressValidationPending}
                    onCancel={() => onCancelEditingOrder?.(order.order_id)}
                    onAssignEndOfDayLoad={() => onAssignEndOfDayLoad?.(order)}
                    onApproveForBilling={() => onApproveForBilling?.(order)}
                    onClearDeliveredOrder={() => onClearDeliveredOrder?.(order)}
                    onEditExecution={() => onEditOrderExecution?.(order)}
                    onChange={(field, value) => onUpdateOrderDraftValue?.(order.order_id, field, value)}
                    onSelectionChange={(isSelected) => onOrderSelectionChange?.(order.order_id, isSelected)}
                    onAddressEnterValidation={() => onOrderAddressEnterValidation?.(order)}
                    onSave={() => onSaveOrder?.(order)}
                    onSaveLoadingReference={onSaveLoadingReference ? (reference) => onSaveLoadingReference(order, reference) : null}
                    onStartEdit={(focusKey) => onStartEditingOrder?.(order, focusKey)}
                    order={order}
                    orderDefaultFocusKey={orderDefaultFocusKey}
                    pinSelectionColumn={pinSelectionColumn}
                    pinnedColumns={pinnedColumns}
                    registerAddressValidationFieldRef={registerAddressValidationFieldRef}
                    scrollableColumns={scrollableColumns}
                    trailingColumnKeySet={trailingColumnKeySet}
                    searchQuery={activeSearchQuery}
                    showSelectionColumn={showSelectionControls}
                    validationError={rowValidationError}
                  />
                );
              })}
            </GridBody>
          </GridTable>
        </div>
        <DataManagerOrdersColumnContextMenu
          canFreezePanes={columnContextMenuState?.canFreezePanes}
          columnKey={columnContextMenuState?.columnKey}
          columnLabel={columnContextMenuState?.columnLabel}
          hasFrozenPanes={hasFrozenPanes}
          isColumnFrozen={columnContextMenuState?.isColumnFrozen}
          isOpen={Boolean(columnContextMenuState)}
          onClose={() => setColumnContextMenuState(null)}
          onToggleColumnFreeze={onToggleColumnFreeze}
          onUnfreezePanes={onUnfreezePanes}
          position={columnContextMenuState}
        />
        </>
      )}
    </section>
  );
}
